/**
 * UsersSection - User management list
 *
 * REFACTORED (Phase 8.2): Uses Zod types from @club-bot/shared/contracts
 * - AdminUserSummary for list items
 * - UserDetailResponseData for detail view
 *
 * Uses shared components:
 * - ListLayout (layouts) - page shell
 * - FilterDropdowns/useFilterPanel (core) - filter system
 * - SortButton (composed) - sort selector
 * - MenuButton (composed) - navigation
 * - InfoCard/InfoCardList (composed) - list items
 * - InfoCardDetail (composed) - box-based detail view
 * - BottomSheet (core) - detail container
 *
 * Detail View Boxes:
 * - Box 1: Identity (static) - telegram ID, username, name
 * - Box 2: Statistics (conditional) - deposits, withdrawals, net
 * - Box 3: Game (static) - game account code
 * - Box 4: Ban Info (conditional) - only if banned
 * - Box 5: Recreated Info (conditional) - only if recreated
 * - Box 6: Actions - ban/unban, recreate buttons
 *
 * Features:
 * - Search by username, telegram ID, name
 * - Filter by status (active/banned)
 * - Sort by date, deposits, withdrawals
 * - Infinite scroll list
 * - Tap to view details
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Users,
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileText,
  Gift,
  Settings,
  Clock,
  ArrowDownAZ,
  ArrowUpAZ,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import {
  ListLayout,
  FilterDropdowns,
  useFilterPanel,
  InfoCard,
  InfoCardList,
  InfoCardDetail,
  BottomSheet,
  PopupMessage,
  cardMatchesSearch,
  findSearchHighlights,
} from '@/components/shared';
import type { FilterCategory, DetailBox } from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';
import type { SortOption } from '@/components/shared/composed/SortButton';
import { useInfiniteScroll } from '@/hooks';

// Import Zod types from shared contracts
import type { AdminUserSummary, UserDetailResponseData } from '@club-bot/shared/contracts';

// ============================================
// BUILD BOXES FUNCTION
// ============================================

interface BuildUserBoxesOptions {
  detail: UserDetailResponseData;
  onBanClick?: () => void;
  onUnbanClick?: () => void;
  onRecreateClick?: () => void;
  actionLoading?: boolean;
}

/** Build user detail boxes from UserDetailResponseData */
function buildUserBoxes({ detail, onBanClick, onUnbanClick, onRecreateClick, actionLoading }: BuildUserBoxesOptions): DetailBox[] {
  const { user, stats, banInfo, recreateInfo } = detail;

  // Check if user has any transactions
  const hasTransactions = (stats?.lifetimeTotalDeposits ?? 0) > 0 || (stats?.lifetimeTotalWithdrawals ?? 0) > 0;

  // Calculate net amount
  const netAmount = hasTransactions
    ? (stats?.lifetimeTotalDeposits ?? 0) - (stats?.lifetimeTotalWithdrawals ?? 0)
    : null;

  return [
    // Box 1: Identity (Static)
    {
      id: 'identity',
      title: 'Identity',
      fields: [
        { label: 'User ID', value: user.telegramId },
        { label: 'Telegram', value: user.username ? `@${user.username}` : null },
        { label: 'Name', value: user.firstName },
        { label: 'Surname', value: user.lastName },
      ],
      visible: true,
    },

    // Box 2: Statistics (Conditional - if has transactions)
    {
      id: 'statistics',
      title: 'Statistics',
      fields: [
        { label: 'Total Deposits', value: stats?.lifetimeTotalDeposits, format: 'currency' },
        { label: 'Total Bonus', value: stats?.currentBonusAmount, format: 'currency' },
        { label: 'Total Withdrawals', value: stats?.lifetimeTotalWithdrawals, format: 'currency' },
        { label: 'Net', value: netAmount, format: 'currency' },
        { label: 'Min Withdrawal', value: stats?.currentMinimumWithdrawal, format: 'currency' },
      ],
      visible: hasTransactions,
    },

    // Box 3: Game (Static)
    {
      id: 'game',
      title: 'Game Account',
      fields: [
        { label: 'Game ID', value: user.gameAccountCode },
        { label: 'Created', value: user.createdAt instanceof Date ? user.createdAt.toISOString() : user.createdAt, format: 'date' },
      ],
      visible: true,
    },

    // Box 4: Ban Info (Conditional - if banned)
    {
      id: 'ban-info',
      title: 'Ban Info',
      fields: [
        { label: 'Banned By', value: banInfo?.bannedBy },
        { label: 'Reason', value: banInfo?.reason },
        { label: 'Banned At', value: banInfo?.bannedAt, format: 'date' },
      ],
      visible: user.isBanned && banInfo !== null,
    },

    // Box 5: Recreated Info (Conditional - if recreated)
    {
      id: 'recreated-info',
      title: 'Recreate History',
      fields: [
        { label: 'Previous Game ID', value: recreateInfo?.oldGameCode },
        { label: 'New Game ID', value: recreateInfo?.newGameCode },
        { label: 'Recreated By', value: recreateInfo?.recreatedBy },
        { label: 'Recreated At', value: recreateInfo?.recreatedAt, format: 'date' },
        { label: 'Reason', value: recreateInfo?.reason },
      ],
      visible: recreateInfo !== null,
    },

    // Box 6: Actions
    {
      id: 'actions',
      fields: [],
      actions: [
        // Ban button (only if not banned)
        ...(onBanClick && !user.isBanned ? [{
          label: 'Ban User',
          onClick: onBanClick,
          variant: 'danger' as const,
          loading: actionLoading,
        }] : []),
        // Unban button (only if banned)
        ...(onUnbanClick && user.isBanned ? [{
          label: 'Unban User',
          onClick: onUnbanClick,
          variant: 'success' as const,
          loading: actionLoading,
        }] : []),
        // Recreate button (always available)
        ...(onRecreateClick ? [{
          label: 'Recreate',
          onClick: onRecreateClick,
          variant: 'warning' as const,
          loading: actionLoading,
        }] : []),
      ],
      visible: !!(onBanClick || onUnbanClick || onRecreateClick),
    },
  ];
}

// ============================================
// COMPONENT PROPS
// ============================================

export interface UsersSectionProps {
  /** List of users (from Zod contract AdminUserSummary) */
  users: AdminUserSummary[];
  /** Loading state */
  isLoading?: boolean;
  /** Whether there are more users to load */
  hasMore?: boolean;
  /** Load more callback */
  onLoadMore?: () => void;
  /** Search callback (for server-side search) */
  onSearch?: (query: string) => void;
  /** Filter callback (for server-side filter) */
  onFilter?: (filters: { status?: string[] }) => void;
  /** Sort callback (for server-side sort) */
  onSort?: (sort: string) => void;
  /** Get full user details (returns UserDetailResponseData from Zod contract) */
  onGetUserDetails?: (telegramId: string) => Promise<UserDetailResponseData>;
  /** Ban user callback (with reason) */
  onBanUser?: (telegramId: string, reason: string) => Promise<void>;
  /** Unban user callback */
  onUnbanUser?: (telegramId: string) => Promise<void>;
  /** Recreate user callback (with reason) */
  onRecreateUser?: (telegramId: string, reason: string) => Promise<void>;
  /** Navigate to other admin pages */
  onNavigate?: (page: string) => void;
}

// Admin menu options
const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'cascade', label: 'Cascade', icon: Settings },
  { value: 'referees', label: 'Referees', icon: Users },
];

// Sort options
const sortOptions: SortOption[] = [
  { value: 'newest', label: 'Newest First', icon: Clock },
  { value: 'oldest', label: 'Oldest First', icon: Clock },
  { value: 'name_asc', label: 'Name (A → Z)', icon: ArrowDownAZ },
  { value: 'name_desc', label: 'Name (Z → A)', icon: ArrowUpAZ },
  { value: 'deposits_high', label: 'Deposits (High → Low)', icon: TrendingUp },
  { value: 'deposits_low', label: 'Deposits (Low → High)', icon: TrendingDown },
];

// Filter categories
const filterCategories: FilterCategory[] = [
  {
    id: 'status',
    label: 'Status',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'banned', label: 'Banned' },
    ],
  },
];

// ============================================
// COMPONENT
// ============================================

export function UsersSection({
  users,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  onSearch,
  onFilter,
  onSort,
  onGetUserDetails,
  onBanUser,
  onUnbanUser,
  onRecreateUser,
  onNavigate,
}: UsersSectionProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Sort state
  const [sortValue, setSortValue] = useState('newest');

  // Scroll container ref for infinite scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Filter panel
  const filterPanel = useFilterPanel(filterCategories);

  // Detail sheet state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<UserDetailResponseData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Popup states
  const [banPopupOpen, setBanPopupOpen] = useState(false);
  const [recreatePopupOpen, setRecreatePopupOpen] = useState(false);

  // Infinite scroll - pass scroll container ref
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: onLoadMore || (() => {}),
    hasMore,
    isLoading,
    scrollRef: scrollContainerRef,
  });

  // Notify parent of filter changes
  useEffect(() => {
    onFilter?.({ status: filterPanel.activeFilters.status });
  }, [filterPanel.activeFilters, onFilter]);

  // Notify parent of sort changes
  useEffect(() => {
    onSort?.(sortValue);
  }, [sortValue, onSort]);

  // Filter users by search query (client-side)
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    return users.filter((user) =>
      cardMatchesSearch(
        {
          telegramId: user.telegramId,
          username: user.username,
          firstName: user.firstName,
          gameAccountCode: user.gameAccountCode,
        },
        searchQuery
      )
    );
  }, [users, searchQuery]);

  // Handle search
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      onSearch?.(query);
    },
    [onSearch]
  );

  // Handle sort
  const handleSort = useCallback((value: string) => {
    setSortValue(value);
  }, []);

  // Handle navigate to detail
  const handleNavigateDetail = useCallback(
    async (telegramId: string) => {
      if (!onGetUserDetails) return;

      setDetailLoading(true);
      setDetailOpen(true);

      try {
        const details = await onGetUserDetails(telegramId);
        setDetailUser(details);
      } catch (error) {
        console.error('Failed to load user details:', error);
      } finally {
        setDetailLoading(false);
      }
    },
    [onGetUserDetails]
  );

  // Open ban popup
  const handleBanClick = useCallback(() => {
    setBanPopupOpen(true);
  }, []);

  // Handle ban confirmation (with reason from popup)
  const handleBanConfirm = useCallback(async (data: { text?: string }) => {
    if (!detailUser || !onBanUser || !data.text) return;

    setBanPopupOpen(false);
    setActionLoading(true);
    try {
      await onBanUser(detailUser.user.telegramId, data.text);
      if (onGetUserDetails) {
        const updated = await onGetUserDetails(detailUser.user.telegramId);
        setDetailUser(updated);
      }
    } catch (error) {
      console.error('Failed to ban user:', error);
    } finally {
      setActionLoading(false);
    }
  }, [detailUser, onBanUser, onGetUserDetails]);

  // Handle unban (no popup needed, just confirmation)
  const handleUnban = useCallback(async () => {
    if (!detailUser || !onUnbanUser) return;

    setActionLoading(true);
    try {
      await onUnbanUser(detailUser.user.telegramId);
      if (onGetUserDetails) {
        const updated = await onGetUserDetails(detailUser.user.telegramId);
        setDetailUser(updated);
      }
    } catch (error) {
      console.error('Failed to unban user:', error);
    } finally {
      setActionLoading(false);
    }
  }, [detailUser, onUnbanUser, onGetUserDetails]);

  // Open recreate popup
  const handleRecreateClick = useCallback(() => {
    setRecreatePopupOpen(true);
  }, []);

  // Handle recreate confirmation (with reason from popup)
  const handleRecreateConfirm = useCallback(async (data: { text?: string }) => {
    if (!detailUser || !onRecreateUser || !data.text) return;

    setRecreatePopupOpen(false);
    setActionLoading(true);
    try {
      await onRecreateUser(detailUser.user.telegramId, data.text);
      if (onGetUserDetails) {
        const updated = await onGetUserDetails(detailUser.user.telegramId);
        setDetailUser(updated);
      }
    } catch (error) {
      console.error('Failed to recreate user:', error);
    } finally {
      setActionLoading(false);
    }
  }, [detailUser, onRecreateUser, onGetUserDetails]);

  // Close detail sheet
  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailUser(null);
  }, []);

  // Get display label for user
  const getUserLabel = (user: AdminUserSummary) => {
    return user.username ? `@${user.username}` : `ID: ${user.telegramId}`;
  };

  // Get search highlights for user
  const getUserHighlights = (user: AdminUserSummary): string[] | undefined => {
    if (!searchQuery) return undefined;
    const highlights = findSearchHighlights(
      {
        telegramId: user.telegramId,
        username: user.username ? `@${user.username}` : null,
        firstName: user.firstName,
        gameAccountCode: user.gameAccountCode,
      },
      searchQuery
    );
    return highlights || undefined;
  };

  // Count active filters
  const activeFilterCount = Object.values(filterPanel.activeFilters).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  return (
    <>
      <ListLayout
        title="Users"
        count={filteredUsers.length}
        countLabel="users"
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search users..."
        onFilterClick={filterPanel.toggleCategoryBox}
        isFilterActive={filterPanel.isCategoryBoxOpen}
        activeFilterCount={activeFilterCount}
        sortOptions={sortOptions}
        sortValue={sortValue}
        onSortChange={handleSort}
        menuOptions={adminMenuOptions}
        menuValue="users"
        onMenuSelect={(page) => onNavigate?.(page)}
        isLoading={isLoading}
        loadingCount={8}
        isEmpty={filteredUsers.length === 0 && !isLoading}
        scrollContainerRef={scrollContainerRef}
        emptyContent={
          <div className="text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-400 text-sm">
              {searchQuery ? 'No users match your search' : 'No users found'}
            </p>
          </div>
        }
        filterContent={
          <FilterDropdowns
            categories={filterCategories}
            activeFilters={filterPanel.activeFilters}
            onFiltersChange={filterPanel.setActiveFilters}
            isCategoryBoxOpen={filterPanel.isCategoryBoxOpen}
            onCategoryBoxClose={filterPanel.closeCategoryBox}
            expandedCategories={filterPanel.expandedCategories}
            onExpandCategory={filterPanel.expandCategory}
            onCollapseCategory={filterPanel.collapseCategory}
          />
        }
      >
        <InfoCardList>
          {filteredUsers.map((user) => (
            <InfoCard
              key={user.telegramId}
              label={getUserLabel(user)}
              type="user"
              searchHighlights={getUserHighlights(user)}
              onClick={() => handleNavigateDetail(user.telegramId)}
            />
          ))}
        </InfoCardList>

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />
      </ListLayout>

      {/* Detail Bottom Sheet - Full Screen */}
      <BottomSheet
        isOpen={detailOpen}
        onClose={handleCloseDetail}
        height="full"
      >
        {detailUser && (
          <InfoCardDetail
            title={detailUser.user.username ? `@${detailUser.user.username}` : detailUser.user.firstName || `User ${detailUser.user.telegramId}`}
            subtitle={detailUser.user.isBanned ? 'Banned' : 'Active'}
            status={detailUser.user.isBanned ? 'banned' : 'active'}
            boxes={buildUserBoxes({
              detail: detailUser,
              onBanClick: onBanUser ? handleBanClick : undefined,
              onUnbanClick: onUnbanUser ? handleUnban : undefined,
              onRecreateClick: onRecreateUser ? handleRecreateClick : undefined,
              actionLoading,
            })}
            onClose={handleCloseDetail}
            isLoading={detailLoading}
          />
        )}
        {detailLoading && !detailUser && (
          <div className="p-4 space-y-4">
            <div className="h-8 w-2/3 bg-slate-200 rounded-lg animate-pulse" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Ban Confirmation Popup */}
      <PopupMessage
        isOpen={banPopupOpen}
        title="Ban User"
        message="Are you sure you want to ban this user? They will no longer be able to access the system."
        showTextInput
        textPlaceholder="Enter reason for ban..."
        textRequired
        minTextLength={15}
        proceedLabel="Ban User"
        proceedVariant="danger"
        declineLabel="Cancel"
        onDecline={() => setBanPopupOpen(false)}
        onProceed={handleBanConfirm}
        isLoading={actionLoading}
      />

      {/* Recreate Confirmation Popup */}
      <PopupMessage
        isOpen={recreatePopupOpen}
        title="Recreate User"
        message="Are you sure you want to recreate this user's game account? This will create a new game account and reset their game data."
        showTextInput
        textPlaceholder="Enter reason for recreate..."
        textRequired
        minTextLength={15}
        proceedLabel="Recreate"
        proceedVariant="warning"
        declineLabel="Cancel"
        onDecline={() => setRecreatePopupOpen(false)}
        onProceed={handleRecreateConfirm}
        isLoading={actionLoading}
      />
    </>
  );
}
